import { useCallback, useMemo, useState } from "react"

import { marketingClient } from "@trueskin-web/apis"
import { Button, Heading, Icon } from "@trueskin-web/components"
import { initialConsultationService } from "@trueskin-web/services"
import { simpleEditIcon } from "@trueskin-web/theme/icons/simpleEditIcon"

import MedicalInfoTags from "./medical-info-tags"
import QuestionnaireOverview from "./questionnaire-overview"

const SKIN_TYPES = {
  Trocken: "Trockene",
  Normal: "Normale",
  Fettig: "Fettige",
  Mischhaut: "Gemischte",
}

const TREATMENT_GOALS = [
  "Akne",
  "Unreinheiten",
  "Rosacea",
  "Feine Linien & Falten",
  "Melasma / Hyperpigmentierung",
  "Neurodermitis",
  "Periorale Dermatitis (POD)",
  "Pigmentflecken",
]

const mapMedicalInfoTags = (qData) => {
  const answers = Object.entries(qData).reduce(
    (accum, [field, { answer }]) => ({
      ...accum,
      [field]: answer,
    }),
    {}
  )

  let tags = []

  if (
    TREATMENT_GOALS.some((condition) =>
      condition.includes(answers.TreatmentGoal)
    )
  ) {
    tags.push(answers.TreatmentGoal)
  }

  tags.push(`${SKIN_TYPES[answers.SkinType] || "Normale"} Haut`)

  if (isNaN(answers.SkinSensitivity)) {
    tags.push(answers.SkinSensitivity)
  } else {
    tags.push(
      parseInt(answers.SkinSensitivity) >= 3 ? "Sensible Haut" : "Normale Haut"
    )
  }

  tags.push(
    answers.hasAllergiesOrIntolerance === "Yes" ? "Allergien" : "Kein Allergien"
  )

  if (answers.IsPregnantOrBreastFeeding) {
    tags.push(
      answers.IsPregnantOrBreastFeeding === "Yes"
        ? "Schwanger"
        : "Nicht schwanger"
    )
  }

  tags = tags
    .filter((tag) => !!tag && !!tag.trim())
    .filter((tag, i, self) => i === self.indexOf(tag))
  tags.push(`+${Object.entries(answers).length - tags.length}`)

  return tags
}

const mapQResponses = (qData) => {
  return Object.entries(qData).map(([field, value]) => {
    let { question, answer } = value

    if (field === "SkinSensitivity" && !isNaN(answer)) {
      answer = `${answer}/5`
    }

    answer = answer === "No" ? "Nein" : answer === "Yes" ? "Ja" : answer

    return {
      question,
      answer,
    }
  })
}

const EditQuestionnaire = ({
  title = "Deine Behandlungsakte",
  onClose,
  children,
}) => {
  const [isOpenDialog, setIsOpenDialog] = useState(false)

  const { api_prefix: _, ...initialConsultation } =
    initialConsultationService.getAll() || {}

  const tags = useMemo(() => mapMedicalInfoTags(initialConsultation), [])

  const responses = useMemo(() => mapQResponses(initialConsultation), [])

  const handleClick = useCallback(() => {
    setIsOpenDialog(true)

    marketingClient.trackEvent({
      name: marketingClient.EVENT_TYPES_ENUM
        .REVIEW_INITIAL_QUESTIONNAIRE_CLICKED,
    })
  }, [])

  const handleDismiss = () => {
    setIsOpenDialog(false)

    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <div
        sx={{
          mb: 2,
          display: "flex",
          columnGap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          color: "text",
        }}
      >
        <Heading
          size="sm"
          sx={{
            fontWeight: "bold",
            lineHeight: "smallerHeading",
          }}
        >
          {title}
        </Heading>

        <Button
          variant="link"
          size="sm"
          onClick={handleClick}
          sx={{ p: 0 }}
          data-testid="edit-q-open-dialog-button"
        >
          <Icon icon={simpleEditIcon} />
        </Button>
      </div>

      {children}

      <MedicalInfoTags tags={tags} />

      <QuestionnaireOverview
        data={responses}
        isOpen={isOpenDialog}
        onDismiss={handleDismiss}
      />
    </>
  )
}

export default EditQuestionnaire
